import { useState, useEffect } from 'react';
import { useChainId } from 'wagmi';
import { orpc } from '@/shared/api/client';
import { contracts } from '@/shared/config';
import type { ChainConfig, EmissionEpoch } from '@/shared/api/db-d1';

// Default vesting schedule: Jan 3, 2026 - Jan 3, 2028 (24 months)
export const DEFAULT_VESTING_START = new Date('2026-01-03T00:00:00Z').getTime();
export const DEFAULT_VESTING_END = new Date('2028-01-03T00:00:00Z').getTime();

// Default emission epochs per chain
export const DEFAULT_EMISSION_EPOCHS: Record<number, EmissionEpoch[]> = {
  // Mainnet: starts with 0 rate (will be configured at launch)
  613419: [
    { startDate: new Date('2026-01-03T00:00:00Z').getTime(), annualEmission: 0 }
  ],
  // Testnet: 10 GNET/day for testing (3650/year)
  843843: [
    { startDate: new Date('2026-01-03T00:00:00Z').getTime(), annualEmission: 3650 }
  ],
};

/**
 * Get the current emission epoch based on the current date.
 * Returns the most recent epoch where startDate <= now, or null if no epoch has started.
 */
export function getCurrentEpoch(epochs: EmissionEpoch[]): EmissionEpoch | null {
  const now = Date.now();
  const sorted = [...epochs].sort((a, b) => b.startDate - a.startDate);
  return sorted.find(e => e.startDate <= now) ?? null;
}

export interface ContractSettings {
  rewardDistributor: string;
  gnetRewardDistributor: string;
  gubi: string;
  votingEscrow: string;
  indexPool: string;
  config: ChainConfig;
}

const getDefaultSettings = (chainId: number): ContractSettings => ({
  rewardDistributor: '',
  gnetRewardDistributor: '',
  gubi: contracts.gubi[chainId as keyof typeof contracts.gubi] || '',
  votingEscrow: contracts.votingEscrow[chainId as keyof typeof contracts.votingEscrow] || '',
  indexPool: contracts.indexPool[chainId as keyof typeof contracts.indexPool] || '',
  config: {},
});

export function useContractSettings() {
  const chainId = useChainId();
  const [settings, setSettings] = useState<ContractSettings>(getDefaultSettings(chainId));
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchSettings = async () => {
      setIsLoading(true);
      try {
        const result = await orpc.settings.get({ chainId });
        if (cancelled) return;

        if (result) {
          setSettings({
            rewardDistributor: result.rewardDistributor,
            gnetRewardDistributor: result.gnetRewardDistributor,
            gubi: result.gubi,
            votingEscrow: result.votingEscrow,
            indexPool: result.indexPool,
            config: result.config || {},
          });
        } else {
          setSettings(getDefaultSettings(chainId));
        }
      } catch (err) {
        console.error('Failed to fetch settings:', err);
        if (!cancelled) {
          setSettings(getDefaultSettings(chainId));
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSettings();

    return () => {
      cancelled = true;
    };
  }, [chainId]);

  // Vesting dates with defaults
  const vestingStart = settings.config.gnetVestingStart ?? DEFAULT_VESTING_START;
  const vestingEnd = settings.config.gnetVestingEnd ?? DEFAULT_VESTING_END;

  // Last gUBI distribution date (null if never distributed)
  const lastGubiDistributionDate = settings.config.lastGubiDistributionDate ?? null;

  // Emission epochs with chain-specific defaults
  const emissionEpochs = settings.config.emissionEpochs ?? (DEFAULT_EMISSION_EPOCHS[chainId] || []);

  return { settings, isLoading, chainId, vestingStart, vestingEnd, lastGubiDistributionDate, emissionEpochs };
}
